import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs, message, Form } from 'antd';
import styles from '../styles/reportDetail.module.less';
import Tab1Content from './components/Tab1Content';
import Tab2Content from './components/Tab2Content';
import ValidateModal from '../ValidateModal';
import { utils } from '@ohif/core';
import {
  normalizeDataIndex,
  formatStudyDate,
  getStateText,
  extractQueryParams
} from '../../../../utils/reportHelper';

const { apiService } = utils;

export interface StudyData {
  register_key?: string;
  bodypart_name?: string;
  modality_code?: string;
  chinese_name?: string;
  sex?: string;
  age?: string;
  study_time?: string;
  check_view2?: string;
  diagnosis_result2?: string;
  yearMonthDay?: string;
  state?: string;
  stateText?: string;
  index?: number;
}

interface AuthState {
  isValidate: boolean;
  modalVisible: boolean;
  isSubmitting: boolean;
}

// 新增 Token 校验状态枚举
type TokenCheckStatus = 'idle' | 'checking' | 'success' | 'failed';

const ReportDetail: React.FC = () => {
  // 核心状态
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<StudyData | null>(null);
  const [studyInstanceUid, setStudyInstanceUid] = useState<string>('');
  const [hideContent, setHideContent] = useState<boolean>(false);
  const [studies, setStudies] = useState<StudyData[]>([]);
  const [listLoading, setListLoading] = useState<boolean>(false);
  const [city, setCity] = useState<string | null>(null);
  const [province, setProvince] = useState<string | null>(null);
  // 新增：Token 校验状态管理（替代直接使用 isTokenReady ref 渲染）
  const [tokenCheckStatus, setTokenCheckStatus] = useState<TokenCheckStatus>('idle');

  // 流程控制锁（保证串行执行）
  const isInitiated = useRef(false); // 防止组件挂载后重复执行初始化流程
  const isTokenReady = useRef(false); // Token 是否就绪标记

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const tokenManager = (window as any).tokenManager;

  // 路由参数解析（仅计算一次）
  const { params: initialParams, firstParamValue } = useMemo(
    () => extractQueryParams(location.search),
    [] // 仅组件挂载时解析一次
  );

  const routeConfig = useMemo(() => ({
    isShareMode: queryParams.get('flag') === 'share',
    hidename: queryParams.get('hidename') === 'true',
    registerKey: queryParams.get('register_key'),
    isEncryptedShare: queryParams.get('isEncryptedShare'),
    code: queryParams.get('code'),
    expiration: queryParams.get('expiration'),
    currentHost: `${window.location.protocol}//${window.location.host}`,
  }), []); // 仅组件挂载时解析一次

  // 验证相关状态（仅初始化一次）
  const storageKey = useMemo(
    () => `isValidate_${firstParamValue}`,
    [firstParamValue]
  );
  const [authState, setAuthState] = useState<AuthState>({
    isValidate: !!localStorage.getItem(storageKey),
    modalVisible: false,
    isSubmitting: false
  });

  // 步骤1：验证分享链接并获取 Token
  const checkShareValidity = useCallback(async (): Promise<boolean> => {
    // 标记为校验中
    setTokenCheckStatus('checking');
    // 非分享模式/加密分享 直接返回成功
    if (!routeConfig.isShareMode || !routeConfig.code || routeConfig.isEncryptedShare !== 'false') {
      // 检查本地是否已有 Token
      isTokenReady.current = !!tokenManager.getCurrentToken();
      setTokenCheckStatus('success');
      return true;
    }

    try {
      const res = await apiService({
        uri: `/api/Share/Access?code=${routeConfig.code}`,
        method: 'GET',
        type: null
      });

      if (res === '查看码无效或已过期。') {
        messageApi.error('分享链接已过期或无效！');
        setHideContent(true);
        isTokenReady.current = false;
        setTokenCheckStatus('failed'); // 标记为校验失败
        return false;
      }

      // 存储 Token 到全局管理器
      if (tokenManager && tokenManager.setToken) {
        tokenManager.setToken(res.Token, res.TokenExpiresAt, res.RefreshToken);
      }
      isTokenReady.current = true;
      setHideContent(false);
      setTokenCheckStatus('success'); // 标记为校验成功
      return true;
    } catch (error) {
      messageApi.error('验证分享链接失败，请刷新页面重试');
      setHideContent(true);
      isTokenReady.current = false;
      setTokenCheckStatus('failed'); // 标记为校验失败
      return false;
    }
  }, []); // 无依赖，仅初始化一次

  // 步骤2：获取定位信息
  const fetchLocation = useCallback(async () => {
    if (!isTokenReady.current) return; // Token 未就绪则跳过

    const cachedLocation = localStorage.getItem('location');
    if (cachedLocation) {
      try {
        const { city, province } = JSON.parse(cachedLocation);
        if (city?.trim() && province?.trim()) {
          setCity(city);
          setProvince(province);
          return;
        }
      } catch (e) {
        console.warn('定位缓存解析失败，将重新请求:', e);
      }
    }

    try {
      const response = await fetch('/getaddress/ip/local/geo/v1/district');
      if (!response.ok) throw new Error(`HTTP请求失败: ${response.status}`);

      const resData = await response.json();
      if (!resData?.data || !resData.data.city || !resData.data.prov) {
        throw new Error('定位接口返回数据格式错误');
      }

      const locationData = {
        city: resData.data.city,
        province: resData.data.prov
      };
      localStorage.setItem('location', JSON.stringify(locationData));
      setCity(locationData.city);
      setProvince(locationData.province);
    } catch (err) {
      console.error('获取定位信息失败:', err);
      setCity('');
      setProvince('');
    }
  }, []);

  // 步骤3：获取检查列表数据
  const fetchStudyList = useCallback(async () => {
    if (!isTokenReady.current || !routeConfig.registerKey) return; // Token/参数未就绪则跳过

    const formData = new FormData();
    const coreParamKeys = [
      'study_id', 'patient_no', 'patient_id', 'in_no', 'out_no',
      'identification_no', 'ybkh', 'jzkh', 'idCard', 'register_key'
    ];

    // 组装参数（仅传 register_key，保证参数唯一）
    formData.append('register_key', routeConfig.registerKey);
    formData.append('cloud_image', '1');

    setListLoading(true);
    try {
      const res = await apiService({
        uri: '/api/reglist/stream-page',
        method: 'POST',
        data: formData,
        type: null
      });

      if (!res?.Data) throw new Error('检查列表数据为空');

      let formattedData = res.Data.map((item: any, index: number) => ({
        ...item,
        index: index + 1
      }));
      formattedData = normalizeDataIndex(formattedData);
      formattedData.sort((a: StudyData, b: StudyData) => {
        const getTimeStamp = (item: StudyData) => {
          if (!item.study_time) return -Infinity;
          const time = new Date(item.study_time).getTime();
          return isNaN(time) ? -Infinity : time;
        };
        return getTimeStamp(b) - getTimeStamp(a);
      });
      formattedData = formattedData.map((item: StudyData) => ({
        ...item,
        yearMonthDay: formatStudyDate(item.study_time || '').yearMonthDay,
        stateText: getStateText(item.state)
      }));
      console.log("formattedData====", formattedData)
      setStudies(formattedData);
      return formattedData; // 返回格式化后的数据
    } catch (error) {
      setStudies([]);
      messageApi.error(
        `获取检查列表失败: ${error instanceof Error ? error.message : '未知错误'}`
      );
    } finally {
      setListLoading(false);
    }
  }, []);

  // 步骤4：获取 StudyInstanceUid 并设置详情数据
  const fetchStudyDetail = useCallback(async (formattedData: StudyData[]) => {
    if (!isTokenReady.current || !routeConfig.registerKey) return; // Token/参数未就绪则跳过

    try {
      // 获取 StudyInstanceUid
      const formData = new FormData();
      formData.append('register_key', routeConfig.registerKey);
      const uid = await apiService({
        uri: '/api/reglist/getSuid',
        method: 'POST',
        data: formData
      });

      console.log("studies=====", formattedData)

      // 从列表中匹配详情数据
      const matchedStudy = formattedData.find(item => item.register_key === routeConfig.registerKey);
      setData(matchedStudy);
      setStudyInstanceUid(uid || '');
    } catch (err) {
      console.error('获取StudyInstanceUid失败:', err);
      messageApi.error('无法获取检查胶片信息，请重试');
    }
  }, []);

  // 核心串行执行流程
  const initFlow = useCallback(async () => {
    // 防止重复执行
    if (isInitiated.current || hideContent) return;
    isInitiated.current = true;
    setLoading(true);

    try {
      // 步骤1：验证分享链接 + 获取 Token
      const shareValid = await checkShareValidity();
      if (!shareValid) return;

      // Token 未就绪则终止流程
      if (!isTokenReady.current) {
        messageApi.error('Token 获取失败，无法继续加载数据');
        return;
      }

      // 步骤2：获取定位信息
      await fetchLocation();

      // 步骤3：获取检查列表
      const formattedData = await fetchStudyList();

      // 步骤4：获取详情数据 + StudyInstanceUid
      await fetchStudyDetail(formattedData);
    } catch (error) {
      messageApi.error(`初始化失败: ${error instanceof Error ? error.message : '未知错误'}`);
    } finally {
      setLoading(false);
    }
  }, [hideContent]);

  // 组件挂载后执行一次串行流程
  useEffect(() => {
    initFlow();

    // 清理函数（防止组件卸载后执行异步操作）
    return () => {
      isInitiated.current = false;
      isTokenReady.current = false;
    };
  }, [initFlow]);

  // 页面跳转逻辑
  const handleGoHistory = useCallback(() => {
    const historyParams = new URLSearchParams(location.search);
    ['register_key', 'flag', 'hidename', 'code', 'expiration', 'from'].forEach(key => {
      historyParams.delete(key);
    });
    const queryString = historyParams.toString();
    navigate(`/userreport${queryString ? `?${queryString}` : ''}`);
  }, [location.search, navigate]);

  return (
    <>
      {contextHolder}
      <div className={styles.containerWrap}>
        <h3 className={styles.title}>胶片详情</h3>
        <div className={styles.container}>
          {!hideContent && !loading && (
            <Tabs
              type="card"
              defaultActiveKey="2"
              items={[
                {
                  key: '1',
                  label: '查看报告',
                  children: (
                    <Tab1Content
                      data={data}
                      hidename={routeConfig.hidename}
                      isShareMode={routeConfig.isShareMode}
                      currentHost={routeConfig.currentHost}
                      onShare={() => { }}
                      onDownload={() => { }}
                      onHistory={handleGoHistory}
                      studyInstanceUid={studyInstanceUid}
                    />
                  ),
                },
                {
                  key: '2',
                  label: '电子胶片',
                  children: (
                    <Tab2Content
                      studyInstanceUid={studyInstanceUid}
                      currentHost={routeConfig.currentHost}
                      city={city}
                      province={province}
                      modality_code={data?.modality_code}
                    />
                  ),
                },
              ]}
              className={styles.customTabs}
              destroyOnHidden={false}
            />
          )}
          {loading && <div>加载中...</div>}
          {tokenCheckStatus === 'failed' && (
            <div>
              分享链接已过期或无效，请重新分享链接！
            </div>
          )}
          {!data && !loading && tokenCheckStatus !== 'failed' && (
            <div>
              数据未加载，请检查链接或返回上一页重试
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ReportDetail;
